import { useSearchStore } from "../../store/searchStore";
import styles from "./ViewToggle.module.scss";

/**
 * Map / list switch for narrow viewports.
 *
 * Below the xl breakpoint the map and the search panel each took roughly half
 * the screen, which left the filters clipped mid-control and the first listing
 * card below the fold. The store has always carried `activeView`; this is the
 * control for it. App.scss reads it off `#appContainer[data-view]` and gives
 * whichever pane is active the full height.
 *
 * Hidden at xl and above, where both panes are shown side by side and
 * `activeView` is ignored.
 */
export default function ViewToggle() {
  const activeView = useSearchStore((s) => s.activeView);
  const setActiveView = useSearchStore((s) => s.setActiveView);
  const totalResults = useSearchStore((s) => s.totalResults);
  const scopedToMap = useSearchStore((s) => s.resultsScoped);

  return (
    <div className={styles.viewToggle} role="group" aria-label="Choose list or map view">
      <button
        type="button"
        className={styles.option}
        aria-pressed={activeView === "list"}
        onClick={() => setActiveView("list")}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <rect x="1" y="2.5" width="14" height="2.2" rx="1" fill="currentColor" />
          <rect x="1" y="6.9" width="14" height="2.2" rx="1" fill="currentColor" />
          <rect x="1" y="11.3" width="14" height="2.2" rx="1" fill="currentColor" />
        </svg>
        List
      </button>

      <button
        type="button"
        className={styles.option}
        aria-pressed={activeView === "map"}
        onClick={() => setActiveView("map")}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path
            d="M5.6 1.6 1.4 3.3v11.1l4.2-1.7 4.8 1.7 4.2-1.7V1.6l-4.2 1.7z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
          <path d="M5.6 1.6v11.1M10.4 3.3v11.1" stroke="currentColor" strokeWidth="1.4" />
        </svg>
        Map
      </button>

      {totalResults > 0 && (
        <span className={styles.count}>
          {totalResults.toLocaleString()}
          <span className={styles.visuallyHidden}>
            {scopedToMap ? " properties in map area" : " properties match"}
          </span>
        </span>
      )}
    </div>
  );
}
